import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JobCard from '../components/JobCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Building2, Users, Star } from 'lucide-react';
import { jobService, publicService } from '../services';
import { api } from '../lib/api';
import { useBannerRotation } from '../hooks/useBannerRotation';
import type { Banner, Job, Partner } from '../types';
import type { PublicStats } from '../services/public.service';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import type { Testimonial } from '../types';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { assetUrl } from '../lib/url';

const MAX_HOME_PARTNERS = 18;

const normalizeBannerText = (value?: string | null) => {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\b(home|vagas|banner|topo|rodape|meio)\b/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

const getBannerAdvertiserKey = (banner?: Banner | null) => {
  if (!banner) return '';
  if (banner.company_id) return `company:${banner.company_id}`;

  if (banner.link) {
    try {
      const host = new URL(banner.link).hostname.replace(/^www\./, '');
      if (host) return `link:${host}`;
    } catch {
      const normalizedLink = normalizeBannerText(banner.link);
      if (normalizedLink) return `link:${normalizedLink}`;
    }
  }

  return `title:${normalizeBannerText(banner.title) || banner.id}`;
};

const takeUniqueBanner = (candidates: Banner[], usedAdvertisers: Set<string>, usedIds: Set<number>) => {
  const unique = candidates.find((banner) => {
    const advertiserKey = getBannerAdvertiserKey(banner);
    return !usedIds.has(banner.id) && !usedAdvertisers.has(advertiserKey);
  });

  const fallback = unique || candidates.find((banner) => !usedIds.has(banner.id));
  if (!fallback) return null;

  usedIds.add(fallback.id);
  usedAdvertisers.add(getBannerAdvertiserKey(fallback));

  return fallback;
};

const takeUniqueBannerGroup = (candidates: Banner[], usedAdvertisers: Set<string>, usedIds: Set<number>, count: number) => {
  const result: Banner[] = [];

  for (const banner of candidates) {
    const advertiserKey = getBannerAdvertiserKey(banner);
    if (usedIds.has(banner.id) || usedAdvertisers.has(advertiserKey)) continue;

    usedIds.add(banner.id);
    usedAdvertisers.add(advertiserKey);
    result.push(banner);

    if (result.length >= count) return result;
  }

  for (const banner of candidates) {
    if (result.length >= count) break;
    if (usedIds.has(banner.id)) continue;

    usedIds.add(banner.id);
    usedAdvertisers.add(getBannerAdvertiserKey(banner));
    result.push(banner);
  }

  return result;
};

const filterUniqueBannerPool = (candidates: Banner[], usedAdvertisers: Set<string>, usedIds: Set<number>) => {
  const seenAdvertisers = new Set<string>();

  return candidates.filter((banner) => {
    const advertiserKey = getBannerAdvertiserKey(banner);
    if (usedIds.has(banner.id) || usedAdvertisers.has(advertiserKey) || seenAdvertisers.has(advertiserKey)) {
      return false;
    }

    seenAdvertisers.add(advertiserKey);
    return true;
  });
};

export default function Home() {
  const navigate = useNavigate();
  const [selectedArea, setSelectedArea] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredJobs, setFeaturedJobs] = useState<Job[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);

  // Sistema de banners com rodízio por tipo
  const { normalBanners, fullwidthBanners, pickFromPool, trackClick, trackImpression } = useBannerRotation({
    rotationInterval: 15000,
  });

  const homeBannerPlacements = useMemo(() => {
    const usedAdvertisers = new Set<string>();
    const usedIds = new Set<number>();
    const topCandidates = pickFromPool(normalBanners, 0, 2);
    const middleCandidates = pickFromPool(normalBanners, 1, 2);
    const bottomCandidates = pickFromPool(normalBanners, 2, 2);
    const fullwidthCandidates = pickFromPool(fullwidthBanners, 0, 1);
    const top = takeUniqueBannerGroup(topCandidates, usedAdvertisers, usedIds, 2);
    const middle = takeUniqueBannerGroup(middleCandidates, usedAdvertisers, usedIds, 2);
    const fullwidth = takeUniqueBanner(fullwidthCandidates, usedAdvertisers, usedIds);
    const bottom = takeUniqueBannerGroup(bottomCandidates, usedAdvertisers, usedIds, 2);
    const inline = filterUniqueBannerPool(normalBanners, usedAdvertisers, usedIds);

    return {
      top,
      inline,
      middle,
      fullwidth,
      bottom,
    };
  }, [fullwidthBanners, normalBanners, pickFromPool]);

  const bannersTop = homeBannerPlacements.top;
  const bannersMiddle = homeBannerPlacements.middle;
  const bannersBottom = homeBannerPlacements.bottom;
  const currentFullwidthBanner = homeBannerPlacements.fullwidth;
  const carouselPartners = useMemo(() => partners.slice(0, MAX_HOME_PARTNERS), [partners]);

  // Registra impressões (uma vez por sessão de página)
  const seenImpressions = useRef<Set<number>>(new Set());
  useEffect(() => {
    const report = (arr: any[]) => {
      arr?.forEach((b) => {
        if (b?.id && !seenImpressions.current.has(b.id)) {
          seenImpressions.current.add(b.id);
          trackImpression(b.id);
        }
      });
    };
    report(bannersTop);
    report(bannersMiddle);
    report(bannersBottom);
    if (currentFullwidthBanner?.id && !seenImpressions.current.has(currentFullwidthBanner.id)) {
      seenImpressions.current.add(currentFullwidthBanner.id);
      trackImpression(currentFullwidthBanner.id);
    }
  }, [bannersTop, bannersMiddle, bannersBottom, currentFullwidthBanner, trackImpression]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) params.set('search', trimmedSearch);
    if (selectedArea) params.set('area', selectedArea);

    const queryString = params.toString();
    navigate(`/vagas${queryString ? `?${queryString}` : ''}`);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Recarrega somente as estatísticas públicas quando a aba volta ao foco
  useEffect(() => {
    const onFocus = () => {
      publicService.getPublicStats().then(setStats).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // A home mostra apenas o recorte priorizado. A listagem completa continua
      // disponível em /vagas, com filtros e paginação no servidor.
      const [jobsResponse, partnersResponse, publicStats] = await Promise.all([
        jobService.getHomeJobs(30),
        api.get<Partner[]>('/public/partners').catch(() => []),
        publicService.getPublicStats().catch(() => null),
      ]);

      // O endpoint /jobs/home já devolve Premium > Destaque > normal.
      setFeaturedJobs(jobsResponse);
      setPartners(Array.isArray(partnersResponse) ? partnersResponse : (partnersResponse as { data?: Partner[] }).data || []);
      setStats(publicStats);
      setTotalJobs(publicStats?.active_jobs || 0);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Registra clique do banner
  const handleBannerClick = (banner: { id: number; link?: string }) => {
    trackClick(banner.id);
    if (banner.link) {
      window.open(banner.link, '_blank');
    }
  };

  // Função para construir URL completa da imagem do banner
  const getBannerImageUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    return assetUrl(imageUrl);
  };

  // Renderiza imagem do banner com versão mobile quando disponível
  const renderBannerImage = (banner: any) => {
    const desktopUrl = getBannerImageUrl(banner.image_url);
    const mobileUrl = banner.mobile_image_url ? getBannerImageUrl(banner.mobile_image_url) : null;

    if (mobileUrl) {
      return (
        <>
          <img src={desktopUrl} alt={banner.title} className="hidden md:block w-full h-full object-contain transition-transform duration-300 hover:scale-[1.03]" />
          <img src={mobileUrl} alt={banner.title} className="md:hidden w-full h-full object-contain transition-transform duration-300 hover:scale-[1.03]" />
        </>
      );
    }
    return <img src={desktopUrl} alt={banner.title} className="w-full h-full object-contain transition-transform duration-300 hover:scale-[1.03]" />;
  };

  // Função para construir URL completa da logo do parceiro
  const getPartnerLogoUrl = (logoUrl: string) => {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return assetUrl(logoUrl);
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <Seo
        title="Vagas Brusque - Empregos em Brusque e Região"
        description="Encontre as melhores vagas de emprego em Brusque e região. Cadastre seu currículo e candidate-se Grátis."
        canonical={window.location.origin + '/'}
        ogImage={window.location.origin + '/logo-vagas.svg'}
      />
      {/* WebSite + SearchAction para caixa de busca nos resultados */}
      <JsonLd
        id="jsonld-website"
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Vagas Brusque',
          url: window.location.origin + '/',
          potentialAction: {
            '@type': 'SearchAction',
            target: window.location.origin + '/vagas?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <Header />

      {/* Hero Section com Busca */}
      <section className="bg-gray-900 relative overflow-hidden">
        <div className="container mx-auto px-4 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Lado esquerdo - Texto e Busca */}
            <div>
              <p className="text-[#87ca1c] mb-2">Encontre sua vaga de emprego</p>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Explore mais de{' '}
                <span className="text-[#87ca1c]">
                  {loading ? '...' : (stats?.active_jobs || totalJobs).toLocaleString('pt-BR')}
                </span>
                <br />
                <span className="text-[#87ca1c]">vagas</span> em Brusque
              </h1>

              {/* Campo de Busca */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-0 text-[#87ca1c] rounded-lg border border-[#87ca1c] sm:h-14 max-w-xl overflow-hidden bg-white p-2 sm:p-0">
                <div className="relative flex-1 min-w-0">
                  <Search className="size-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Cargo, empresa ou palavra-chave"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    className="bg-transparent border-0 shadow-none text-gray-800 h-11 sm:h-full pl-9 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
                  />
                </div>

                <Select value={selectedArea} onValueChange={setSelectedArea}>
                  <SelectTrigger className="bg-transparent border-0 sm:border-l sm:border-gray-200 text-gray-800 h-11 sm:h-full w-full sm:w-[210px] flex-shrink-0 focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder="Área de interesse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Comercial/Vendas</SelectItem>
                    <SelectItem value="2">Administração</SelectItem>
                    <SelectItem value="3">Tecnologia</SelectItem>
                    <SelectItem value="4">Produção</SelectItem>
                    <SelectItem value="8">Marketing</SelectItem>
                    <SelectItem value="6">Recursos Humanos</SelectItem>
                    <SelectItem value="5">Logística</SelectItem>
                    <SelectItem value="7">Financeiro</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  type="button"
                  className="bg-[#87ca1c] hover:bg-brand-900 h-11 sm:h-8 px-4 sm:mr-1 rounded-md flex-shrink-0"
                  onClick={handleSearch}
                >
                  <Search className="size-4 mr-1" />
                  Buscar
                </Button>
              </div>

              {/* Estatísticas */}
              <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-6">
                <div className="flex items-center gap-1.5">
                  <Building2 className="size-4 text-brand-700" />
                  <span className="text-[#87ca1c] font-bold">
                    {loading ? '...' : (stats?.total_companies || 0)}
                  </span>
                  <span className="text-white text-sm">Empresas</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="size-4 text-amber-500" />
                  <span className="text-[#87ca1c] font-bold">
                    {loading ? '...' : (stats?.active_jobs || 0)}
                  </span>
                  <span className="text-white text-sm">Vagas ativas</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="size-4 text-green-600" />
                  <span className="text-[#87ca1c] font-bold">
                    {loading ? '...' : (stats?.total_candidates || 0)}
                  </span>
                  <span className="text-white text-sm">Candidatos</span>
                </div>
              </div>
            </div>

            {/* Lado direito - Imagem */}
            <div className="hidden lg:block absolute bottom-0 lg:right-[15%] xl:right-[20%] 2xl:right-[25%]">
              <img
                src="/mulher-capa.png"
                alt="Profissional"
                className="h-[350px] xl:h-[400px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="w-full h-1 bg-gradient-to-r from-[#0]" />

      {/* Banner Publicitário - Topo (home-top) */}
      {bannersTop.length > 0 && (
        <section className="py-8 bg-gray-200">
          <div className="container mx-auto px-4">
            <div className={`grid grid-cols-1 ${bannersTop.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
              {bannersTop.map((banner, index) => (
                <div
                  key={`home-top-${banner.id}-${index}`}
                  className={`rounded-xl overflow-hidden cursor-pointer bg-gray-200 ${index > 0 ? 'hidden md:block' : ''}`}
                  onClick={() => handleBannerClick(banner)}
                >
                  {renderBannerImage(banner)}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Vagas Disponíveis */}
      <section className="mb-5 bg-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {loading ? (
              <p className="text-gray-500">Carregando vagas...</p>
            ) : featuredJobs && featuredJobs.length > 0 ? (
              featuredJobs.flatMap((job, index) => {
                const elements = [<JobCard key={job.id} job={job} />];
                // Intercala banner a cada 9 vagas
                if ((index + 1) % 9 === 0 && homeBannerPlacements.inline.length > 0) {
                  const bannerIndex = Math.floor(index / 9);
                  const inlineBanner = homeBannerPlacements.inline[bannerIndex];
                  if (inlineBanner) {
                    elements.push(
                      <div
                        key={`inline-banner-${inlineBanner.id}-${index}`}
                        className="col-span-1 sm:col-span-2 md:col-span-3 rounded-xl overflow-hidden cursor-pointer bg-gray-100"
                        onClick={() => handleBannerClick(inlineBanner)}
                      >
                        {renderBannerImage(inlineBanner)}
                      </div>
                    );
                  }
                }
                return elements;
              })
            ) : (
              <p className="text-gray-500">Nenhuma vaga disponível no momento.</p>
            )}
          </div>
          {!loading && featuredJobs.length > 0 && (
            <div className="mt-8 text-center">
              <Button asChild variant="outline">
                <Link to="/vagas">Ver todas as vagas</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Banner Publicitário - Meio (home-middle) */}
      {bannersMiddle.length > 0 && (
        <section className="py-8 bg-gray-200">
          <div className="container mx-auto px-4">
            <div className={`grid grid-cols-1 ${bannersMiddle.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
              {bannersMiddle.map((banner, index) => (
                <div
                  key={`home-middle-${banner.id}-${index}`}
                  className={`rounded-xl overflow-hidden cursor-pointer bg-gray-200 ${index > 0 ? 'hidden md:block' : ''}`}
                  onClick={() => handleBannerClick(banner)}
                >
                  {renderBannerImage(banner)}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Banner Grande em Destaque - Full Width */}
      {currentFullwidthBanner && (
        <section
          className="w-full overflow-hidden cursor-pointer relative bg-gray-200"
          onClick={() => handleBannerClick(currentFullwidthBanner)}
        >
          {renderBannerImage(currentFullwidthBanner)}
        </section>
      )}

      {/* Parceiros */}
      <section className="py-16 bg-white">
        <div className="mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900">
              As melhores empresas <span className="text-[#87ca1c]">anunciam suas vagas aqui</span>
            </h2>
          </div>

          {carouselPartners.length > 0 && (
            <Swiper
              modules={[Autoplay]}
              spaceBetween={40}
              slidesPerView={2}
              loop={carouselPartners.length >= 2}
              speed={800}
              autoplay={{
                delay: 2000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                640: { slidesPerView: 3 },
                768: { slidesPerView: 4 },
                1024: { slidesPerView: 5 },
                1280: { slidesPerView: 6 },
              }}
              className="py-4"
            >
              {/* Replica os parceiros para garantir slides suficientes para o loop */}
              {(() => {
                // Precisa ter pelo menos 7 slides para loop funcionar bem em telas grandes (6 + 1)
                const minSlides = 7;
                let slides = [...carouselPartners];
                while (slides.length < minSlides) {
                  slides = [...slides, ...carouselPartners];
                }
                return slides.map((partner, index) => (
                  <SwiperSlide key={`partner-${partner.id}-${index}`}>
                    <div
                      className="flex items-center justify-center h-24 grayscale opacity-70 pointer-events-none"
                    >
                      <img
                        src={getPartnerLogoUrl(partner.logo_url)}
                        alt={partner.name}
                        className="max-h-20 w-auto object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </SwiperSlide>
                ));
              })()}
            </Swiper>
          )}
        </div>
      </section>

      {/* Banner Publicitário - Rodapé (home-bottom) */}
      {bannersBottom.length > 0 && (
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className={`grid grid-cols-1 ${bannersBottom.length > 1 ? 'md:grid-cols-2' : ''} gap-4`}>
              {bannersBottom.map((banner, index) => (
                <div
                  key={`home-bottom-${banner.id}-${index}`}
                  className={`rounded-xl overflow-hidden cursor-pointer bg-gray-200 ${index > 0 ? 'hidden md:block' : ''}`}
                  onClick={() => handleBannerClick(banner)}
                >
                  {renderBannerImage(banner)}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Depoimentos dinâmicos */}
      <DynamicTestimonials />

      <Footer />
    </div>
  );
}

function DynamicTestimonials() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await publicService.getTestimonials();
        setItems(resp || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return null;
  if (!items || items.length === 0) return null;

  const getAvatar = (t: Testimonial) => {
    const anyT: any = t as any;
    const logo =
      anyT?.company?.logo ||
      anyT?.company?.logo_url ||
      anyT?.logo ||
      anyT?.logo_url ||
      anyT?.company_logo ||
      anyT?.candidate?.avatar ||
      anyT?.candidate?.photo ||
      anyT?.avatar ||
      anyT?.photo ||
      anyT?.image ||
      '';
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    const apiBase = (import.meta as any).env?.VITE_API_URL || '/api';
    const origin = apiBase.startsWith('http')
      ? new URL(apiBase, window.location.origin).origin
      : window.location.origin;
    return `${origin}${logo.startsWith('/') ? '' : '/'}${logo}`;
  };
  const getAuthorName = (t: Testimonial) => {
    const anyT: any = t as any;
    return anyT?.company?.name || anyT?.candidate?.name || anyT?.author || anyT?.author_name || anyT?.name || anyT?.company_name || anyT?.candidate_name || 'Autor';
  };
  const getContent = (t: Testimonial) => {
    const anyT: any = t as any;
    return anyT?.content || anyT?.message || anyT?.text || '';
  };

  return (
    <section className="py-16 bg-[#87ca1c]">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-center">
          <div className="lg:col-span-1">
            <h2 className="text-3xl font-bold mb-2">
              <span className="text-white">Boas</span> <span className="text-gray-900">experiências</span> <span className="text-white">deixam marcas</span>
            </h2>
            <p className="text-gray-500">Veja o que estão falando da gente</p>
          </div>
          <div className="lg:col-span-3">
            <Swiper modules={[Autoplay]} spaceBetween={24} slidesPerView={1} loop autoplay={{ delay: 4000, disableOnInteraction: false }} breakpoints={{ 640: { slidesPerView: 2 }, 1024: { slidesPerView: 3 }}}>
              {items.filter(t => !!getContent(t)).map((t, index) => (
                <SwiperSlide key={(t as any).id || index}>
                  <div className="bg-gray-50 rounded-lg p-6 border h-64 flex flex-col">
                    <div className="text-brand-300 text-4xl font-serif mb-2">"</div>
                    <p className="text-gray-700 text-sm mb-6 line-clamp-4 flex-1">{getContent(t)}</p>
                    <div className="flex items-center gap-3 mt-auto">
                      <div className="size-10 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                        {getAvatar(t) ? (
                          <ImageWithFallback src={getAvatar(t) as string} alt={getAuthorName(t)} className="size-full object-contain" />
                        ) : (
                          <span className="text-gray-500 text-xs font-bold">
                            {getAuthorName(t).charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-brand-600 text-sm">{getAuthorName(t)}</p>
                        <p className="text-gray-500 text-xs uppercase">{(t as any).company ? 'Empresa' : 'Candidato'}</p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
}
